import React, { CSSProperties, Dispatch, SetStateAction, useState } from 'react';

import { Drawer } from '@mui/material';
import { useIsMobile } from '../components/shared';

const LOGS_OFFSET_WIDTH_LS_KEY = "centerLogsWidth";
const LOGS_OFFSET_HEIGHT_LS_KEY = "centerLogsHeight";

interface IResizableDrawerProps {
    direction?: 'right' | 'bottom',
    open: boolean,
    handleClose: Dispatch<SetStateAction<boolean>>,
    children: React.ReactNode
}

const getDraggerStyle = (direction: 'right' | 'bottom'): CSSProperties => {
    if (direction === 'bottom') {
        return {
            height: '5px',
            width: '100%',
            cursor: 'ns-resize',
            padding: '0 4px',
            borderLeft: '1px solid #ddd',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: '100',
            backgroundColor: '#f4f7f9'
        };
    }
    return {
        width: '5px',
        cursor: 'ew-resize',
        padding: '4px 0 0',
        borderTop: '1px solid #ddd',
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: '100',
        backgroundColor: '#f4f7f9'
    };
}

const ResizableDrawer = (props: IResizableDrawerProps) => {
    const { direction = 'right', children, open, handleClose } = props;
    const isMobile = useIsMobile();

    const [state, setState] = useState(() => {
        const widthRaw = localStorage.getItem(LOGS_OFFSET_WIDTH_LS_KEY);
        const heightRaw = localStorage.getItem(LOGS_OFFSET_HEIGHT_LS_KEY);
        const width = isMobile ? 100 : widthRaw ? parseInt(widthRaw) : 20;
        const height = isMobile ? 100 : heightRaw ? parseInt(heightRaw) : 20;
        return {
            isResizing: false,
            width,
            height
        }
    });
    const onResizeStart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setState(s => ({
            ...s,
            isResizing: true
        }));
        window.addEventListener('mousemove', onResize);
        window.addEventListener('mouseup', onResizeEnd);
    };
    const onResize = (e: MouseEvent) => {
        setState(s => {
            if (!s.isResizing) return s;
            if (direction === 'bottom') {
                const offsetBottom =
                    document.body.offsetHeight - (e.clientY - document.body.offsetTop);
                const heightPercent = Math.round(offsetBottom / document.body.offsetHeight * 100);
                if (heightPercent < 100 && heightPercent > 20) {
                    localStorage.setItem(LOGS_OFFSET_HEIGHT_LS_KEY, heightPercent.toString())
                    return { ...s, height: heightPercent };
                }
                return s;
            }
            const offsetRight =
                document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
            const widthPercent = Math.round(offsetRight / document.body.offsetWidth * 100);
            if (widthPercent < 100 && widthPercent > 20) {
                localStorage.setItem(LOGS_OFFSET_WIDTH_LS_KEY, widthPercent.toString())
                return { ...s, width: widthPercent };
            }
            return s;
        });
    };
    const onResizeEnd = () => {
        setState(s => ({ ...s, isResizing: false }));
        window.removeEventListener('mousemove', onResize);
        window.removeEventListener('mouseup', onResizeEnd);
    };

    const paperStyle: CSSProperties = direction === 'bottom'
        ? { height: state.height + "%" }
        : { width: state.width + "%" };

    return (
        <Drawer
            variant="temporary"
            anchor={direction}
            open={open}
            onClose={() => handleClose(false)}
            ModalProps={{
                keepMounted: true // Better open performance on mobile.
            }}
            PaperProps={{ style: paperStyle }}
        >
            <div
                id="dragger"
                onMouseDown={onResizeStart}
                style={getDraggerStyle(direction)}
            />
            {children}
            </Drawer>
    )
}

export default ResizableDrawer;